import { Translations } from './fr';

export const en: Translations = {
  // Navigation
  nav: {
    home: "Home",
    primeEvolution: "Evolution",
    pulse: "Pulse",
    sparkle: "Sparkle",
    agents: "Agents",
    partners: "Partners",
    contact: "Contact",
    recruitAgent: "Recruit an Agent",
  },

  // Hero
  hero: {
    title: "Recruit our",
    titleHighlight: "AI Agents",
    titleEnd: "to Strengthen Your Teams",
    subtitle: "Virtual collaborators available 24/7, trained on your business processes, ready to transform your productivity.",
    cta: "Recruit an AI Agent",
    discover: "Discover Prime Evolution",
  },

  // Trust Banner
  trust: {
    available: "Available 24/7",
    secure: "100% Secure",
    agents: "Certified Agents",
    support: "Dedicated Support",
  },

  // Prime Evolution
  primeEvolution: {
    badge: "Our Flagship Solution",
    title: "Prime Evolution",
    subtitle: "The complete support program to integrate AI into your business",
    description: "Prime Evolution is our holistic approach combining audit, training, and deployment of custom AI agents to sustainably transform your organization.",
    features: {
      audit: {
        title: "Strategic Audit",
        description: "In-depth analysis of your processes to identify high-impact automation opportunities.",
      },
      training: {
        title: "Team Training",
        description: "Tailored training programs so your teams can master and collaborate effectively with AI.",
      },
      deployment: {
        title: "Custom Deployment",
        description: "Personalized AI agents integrated with your existing tools and trained on your business data.",
      },
      support: {
        title: "Continuous Support",
        description: "Dedicated support and continuous optimization to maximize your return on investment.",
      },
    },
    cta: "Start Prime Evolution",
    stats: {
      productivity: "Average productivity gain",
      roi: "Return on investment",
      satisfaction: "Client satisfaction rate",
    },
  },

  // TagPDF
  sparkle: {
    badge: "Intelligent Digitization",
    title: "Sparkle",
    subtitle: "TagPDF v7 Powered by AI",
    description: "Transform your paper documents into usable data with our intelligent digitization solution. Automatic recognition, data extraction, and intelligent classification.",
    features: {
      scan: {
        title: "Advanced Digitization",
        description: "High-precision OCR for all document types.",
      },
      ai: {
        title: "Integrated AI",
        description: "Automatic classification and extraction of key data.",
      },
      speed: {
        title: "Ultra-Fast Processing",
        description: "Thousands of pages processed in minutes.",
      },
      accuracy: {
        title: "Maximum Accuracy",
        description: "Recognition rate exceeding 99%.",
      },
    },
    cta: "Discover Sparkle",
    ctaFulltext: "Full-text Indexing",
    ctaFulltextDesc: "Search instantly across all your documents.",
    ctaScan: "Lightning Scan → Folder",
    ctaScanDesc: "Scan and organize in the blink of an eye.",
  },

  // Pulse
  pulse: {
    badge: "Intelligent Automation",
    title: "VEECTORIA Pulse",
    subtitle: "Automation that pulses to the rhythm of your business",
    description: "Pulse is our intelligent automation platform that connects, orchestrates, and optimizes your workflows in real-time.",
    features: {
      connect: {
        title: "Connect",
        description: "Native integration with over 200 professional tools and platforms.",
      },
      orchestrate: {
        title: "Orchestrate",
        description: "Intelligent workflows that automatically adapt to your needs.",
      },
      optimize: {
        title: "Optimize",
        description: "Real-time analysis and recommendations to improve your processes.",
      },
    },
    cta: "Discover Pulse",
  },

  // Advantages
  advantages: {
    badge: "Why Choose Us",
    title: "VEECTORIA Advantages",
    subtitle: "A unique approach combining human expertise and AI power",
    items: {
      expertise: {
        title: "Recognized Expertise",
        description: "Over 10 years of experience in digital transformation and AI.",
      },
      custom: {
        title: "Custom Solutions",
        description: "Each agent is specifically trained on your processes and data.",
      },
      security: {
        title: "Maximum Security",
        description: "Data hosted in Europe, GDPR compliance guaranteed.",
      },
      roi: {
        title: "Guaranteed ROI",
        description: "Commitment to results with measurable indicators.",
      },
      support: {
        title: "24/7 Support",
        description: "Dedicated team available at all times to support you.",
      },
      evolution: {
        title: "Continuous Evolution",
        description: "Your agents constantly improve through machine learning.",
      },
    },
  },

  // Testimonials
  testimonials: {
    badge: "Testimonials",
    title: "They Trust Us",
    subtitle: "Discover how our clients transform their business with VEECTORIA",
    items: [
      { quote: "Thanks to VEECTORIA, we reduced admin tasks by 70%.", metric: "-70%", metricLabel: "admin time", name: "Isabelle G.", role: "Admin Manager" },
      { quote: "The commercial AI agent transformed our prospecting. +40% qualified appointments.", metric: "+40%", metricLabel: "qualified appointments", name: "Marc D.", role: "Sales Director" },
      { quote: "The document solution saves us precious time on file management.", metric: "5x", metricLabel: "faster", name: "Sophie M.", role: "Manager" },
    ],
  },

  // FAQ
  faq: {
    badge: "Frequently Asked Questions",
    title: "Everything About VEECTORIA",
    subtitle: "Answers to your most common questions",
    items: {
      q1: {
        question: "What is a VEECTORIA AI Agent?",
        answer: "A VEECTORIA AI Agent is an intelligent virtual collaborator trained on your specific business processes. It can handle various tasks: customer support, data analysis, writing, administrative management, and much more.",
      },
      q2: {
        question: "How long does it take to deploy an agent?",
        answer: "Standard deployment takes between 2 and 4 weeks, including initial audit, configuration, training on your data, and testing. For more complex needs, allow 6 to 8 weeks.",
      },
      q3: {
        question: "Is my data secure?",
        answer: "Absolutely. All data is hosted in Europe on certified servers. We are 100% GDPR compliant and apply the highest security standards (encryption, controlled access, regular audits).",
      },
      q4: {
        question: "What is the expected return on investment?",
        answer: "Our clients see an average productivity gain of 40% and positive ROI within the first 3 months. We commit to measurable results.",
      },
      q5: {
        question: "How does integration with our existing tools work?",
        answer: "VEECTORIA natively integrates with over 200 professional tools (CRM, ERP, communication tools, etc.). Our technical team ensures smooth integration without disrupting your operations.",
      },
    },
  },

  // Contact
  contact: {
    badge: "Contact Us",
    title: "Ready to Transform Your Business?",
    subtitle: "Let's discuss your needs and discover how VEECTORIA can support you",
    form: {
      name: "Full name",
      email: "Professional email",
      company: "Company",
      phone: "Phone",
      message: "Your message",
      submit: "Send message",
      sending: "Sending...",
      success: "Message sent successfully!",
      error: "Error sending. Please try again.",
    },
    info: {
      title: "Contact Information",
      email: "Email",
      phone: "Phone",
      address: "Address",
    },
  },

  // CTA
  cta: {
    title: "Ready to Take Action?",
    subtitle: "Join the companies that have already transformed their productivity with VEECTORIA",
    button: "Recruit an AI Agent",
  },

  // Footer
  footer: {
    description: "Artificial intelligence solutions to transform your business.",
    navigation: "Navigation",
    legal: "Legal Notice",
    privacy: "Privacy Policy",
    terms: "Terms and Conditions",
    cookies: "Cookie Settings",
    rights: "All rights reserved.",
  },

  // Agents Carousel
  agents: {
    badge: "Our Team",
    title: "Our AI Agents",
    subtitle: "Discover our virtual collaborators ready to join your team",
  },

  // AI Agents Showcase
  agentsShowcase: {
    badge: "AI Excellence at Your Service",
    title: "Our AI Agents Transform Your Daily Work",
    description: "Imagine a team of virtual collaborators that never sleeps, continuously learns, and perfectly adapts to your business processes. Our AI Agents amplify your capabilities, free your talents from repetitive tasks, and propel your productivity to new heights.",
    imageAlt: "VEECTORIA AI Agents Team",
    cta: "Discover Our Agents",
    benefits: {
      availability: "Available 24/7",
      productivity: "10x Productivity",
      performance: "Optimal Performance",
      collaboration: "Seamless Collaboration",
    },
  },

  // Cookie consent
  cookies: {
    title: "Cookie Management",
    message: "We use cookies to improve your experience on our site, analyze traffic, and personalize content. You can customize your preferences or accept all cookies.",
    accept: "Accept all",
    decline: "Decline",
    settings: "Customize",
    settingsTitle: "Cookie Settings",
    settingsDescription: "Manage your cookie preferences. Essential cookies are necessary for the site to function and cannot be disabled.",
    saveChoices: "Save my choices",
    rejectAll: "Reject all",
    alwaysActive: "Always active",
    categories: {
      essential: { name: "Essential cookies", description: "These cookies are necessary for the site to function and cannot be disabled. They enable navigation and access to basic features." },
      analytics: { name: "Analytics cookies", description: "These cookies allow us to measure site audience and analyze browsing behavior to improve our services." },
      marketing: { name: "Marketing cookies", description: "These cookies are used to offer personalized ads and measure the effectiveness of our marketing campaigns." },
      preferences: { name: "Preference cookies", description: "These cookies remember your preferences (language, theme, region) to personalize your experience on the site." },
    },
  },

  // Partners Section
  partners: {
    badge: "Partner Program",
    title: "Let's Build Together",
    description: "Are you a reseller, distributor, software editor, or wholesaler? VEECTORIA becomes your preferred technology partner to conquer new markets with cutting-edge artificial intelligence solutions.",
    valueProposition: "We design and develop complete, customized professional solutions for your clients, while supporting you in implementing a mass commercialization strategy. Attractive margins, dedicated technical support, and marketing assistance: all the ingredients for your success.",
    types: {
      resellers: {
        title: "Resellers",
        description: "Integrate our AI solutions into your catalog and offer differentiating packages to your clients.",
      },
      distributors: {
        title: "Distributors",
        description: "Deploy our technologies at scale in your territory with privileged support.",
      },
      editors: {
        title: "Software Editors",
        description: "Enhance your software with our AI and create unparalleled added value.",
      },
      wholesalers: {
        title: "Wholesalers",
        description: "Access preferential rates for mass distribution through your networks.",
      },
    },
    benefits: {
      title: "Your Partner Benefits",
      whiteLabel: "White-label solutions",
      support: "Dedicated technical support",
      scalability: "Unlimited scalability",
    },
    cta: "Become a Partner",
    ctaSubtext: "Contact us to discover our exclusive partner program",
  },

  // Legal modals
  legal: {
    lastUpdate: "Last updated: January 2026",
    mentions: {
      title: "Legal Notice",
      sections: {
        editor: {
          title: "1. Site Publisher",
          intro: "This site is published by:",
          companyName: "Company name",
          legalForm: "Legal form",
          capital: "Share capital",
          address: "Registered office",
          siren: "SIREN",
          siret: "SIRET",
          rcs: "RCS",
          tva: "VAT Number",
          naf: "NAF Code",
        },
        publication: {
          title: "2. Publication Director",
          intro: "Publication is managed by:",
          since: "since",
        },
        contact: {
          title: "3. Contact",
          phone: "Phone",
          email: "Email",
        },
        hosting: {
          title: "4. Hosting",
          intro: "The site is hosted by:",
          name: "Name",
          address: "Address",
        },
        intellectual: {
          title: "5. Intellectual Property",
          content1: "This entire site is subject to French and international legislation on copyright and intellectual property. All reproduction rights are reserved, including downloadable documents and iconographic and photographic representations.",
          content2: "Reproduction of all or part of this site on any medium is strictly prohibited without express authorization from",
        },
        liability: {
          title: "6. Limitation of Liability",
          content: "The information on this site is as accurate as possible and the site is periodically updated, but may contain inaccuracies, omissions or gaps. {company} cannot be held responsible for direct or indirect damages resulting from the use of this site.",
        },
        law: {
          title: "7. Applicable Law",
          content: "This site and legal notices are subject to French law. In case of dispute, French courts shall have exclusive jurisdiction.",
        },
      },
    },
    privacy: {
      title: "Privacy Policy",
      sections: {
        intro: {
          title: "1. Introduction",
          content: "{company} is committed to protecting the privacy of its website users. This privacy policy explains how we collect, use and protect your personal data in accordance with the General Data Protection Regulation (GDPR).",
        },
        controller: {
          title: "2. Data Controller",
          company: "Company",
          address: "Address",
          email: "Email",
        },
        data: {
          title: "3. Data Collected",
          intro: "We collect the following data:",
          items: [
            "Identification data: name, surname, email, phone",
            "Professional data: company, position",
            "Navigation data: IP address, cookies, pages visited",
            "Communication data: messages via contact forms",
          ],
        },
        purposes: {
          title: "4. Processing Purposes",
          intro: "Your data is collected to:",
          items: [
            "Respond to your contact and demonstration requests",
            "Provide our AI services and solutions",
            "Improve our site and services",
            "Send commercial communications (with your consent)",
            "Comply with our legal obligations",
          ],
        },
        basis: {
          title: "5. Legal Basis",
          intro: "The processing of your data is based on:",
          items: [
            "Your explicit consent",
            "Contract execution",
            "Our legitimate interest",
            "Compliance with legal obligations",
          ],
        },
        retention: {
          title: "6. Retention Period",
          content: "Your personal data is kept for the duration necessary for the purposes for which it was collected, and for a maximum of 3 years after your last contact with us.",
        },
        rights: {
          title: "7. Your Rights",
          intro: "In accordance with GDPR, you have the following rights:",
          access: "Right of access",
          accessDesc: "obtain a copy of your data",
          rectification: "Right of rectification",
          rectificationDesc: "correct inaccurate data",
          erasure: "Right to erasure",
          erasureDesc: "request deletion of your data",
          restriction: "Right to restriction",
          restrictionDesc: "limit processing of your data",
          portability: "Right to portability",
          portabilityDesc: "receive your data in a structured format",
          objection: "Right to object",
          objectionDesc: "object to the processing of your data",
          contact: "To exercise these rights, contact us at:",
        },
        security: {
          title: "8. Security",
          content: "We implement appropriate technical and organizational measures to protect your data against unauthorized access, modification, disclosure or destruction.",
        },
        complaint: {
          title: "9. Complaint",
          content: "If you believe your rights are not being respected, you can lodge a complaint with the CNIL (Commission Nationale de l'Informatique et des Libertés): www.cnil.fr",
        },
      },
    },
    cgv: {
      title: "Terms and Conditions",
      sections: {
        object: {
          title: "1. Purpose",
          content: "These Terms and Conditions govern the contractual relationships between {company} and its clients for the provision of artificial intelligence services, software development and business solutions.",
        },
        provider: {
          title: "2. Provider",
          company: "Company",
          siret: "SIRET",
          address: "Address",
        },
        services: {
          title: "3. Services Offered",
          intro: "{company} offers the following services:",
          items: [
            "Specialized AI agents (transcription, management, support, etc.)",
            "Custom business application development (Prime Evolution)",
            "Document AI solutions",
            "VEECTORIA Pulse - Transcription and reporting platform",
            "AI solution consulting and integration",
          ],
        },
        quotes: {
          title: "4. Quotes and Orders",
          content: "All services are subject to a detailed quote. The order is considered final upon signature of the quote and receipt of the agreed deposit.",
        },
        pricing: {
          title: "5. Pricing and Payment",
          content1: "Prices are quoted in euros excluding taxes. Applicable VAT is 20%. Unless otherwise stated, invoices are payable within 30 days of month end.",
          content2: "In case of late payment, late payment penalties will be applied at 3 times the legal interest rate, plus a fixed indemnity of €40 for collection costs.",
        },
        delivery: {
          title: "6. Delivery and Deadlines",
          content: "Delivery times are indicative. {company} commits to respecting the deadlines agreed in the quote, except in exceptional circumstances.",
        },
        intellectual: {
          title: "7. Intellectual Property",
          content: "Unless otherwise stipulated, developments made by {company} remain its property until full payment of the service. After payment, usage rights are transferred to the client according to the terms defined in the contract.",
        },
        confidentiality: {
          title: "8. Confidentiality",
          content: "Each party undertakes to keep confidential the information exchanged within the framework of the commercial relationship for a period of 3 years after the end of the contract.",
        },
        liability: {
          title: "9. Limitation of Liability",
          content: "The liability of {company} is limited to the amounts actually received under the contract concerned. {company} cannot be held responsible for indirect damages.",
        },
        law: {
          title: "10. Applicable Law",
          content: "These Terms and Conditions are subject to French law. Any dispute shall be brought before the competent courts of Paris.",
        },
      },
    },
    cookiesPolicy: {
      title: "Cookie Policy",
      sections: {
        what: {
          title: "1. What is a cookie?",
          content: "A cookie is a small text file placed on your device (computer, tablet, smartphone) when visiting a website. It allows the site to remember information about your visit.",
        },
        types: {
          title: "2. Cookies Used",
          intro: "Our site uses the following types of cookies:",
          essential: "Essential cookies",
          essentialDesc: "Necessary for the site to function. They cannot be disabled.",
          analytics: "Analytics cookies",
          analyticsDesc: "Allow us to understand how you use our site to improve it.",
          preferences: "Preference cookies",
          preferencesDesc: "Remember your choices (language, theme) to personalize your experience.",
        },
        retention: {
          title: "3. Retention Period",
          content: "Cookies are kept for a maximum of 13 months in accordance with CNIL recommendations.",
        },
        management: {
          title: "4. Cookie Management",
          intro: "You can change your cookie preferences at any time via your browser settings:",
          items: [
            "Chrome: Settings → Privacy and security → Cookies",
            "Firefox: Options → Privacy and security → Cookies",
            "Safari: Preferences → Privacy → Cookies",
            "Edge: Settings → Cookies and site permissions",
          ],
        },
        contact: {
          title: "5. Contact",
          content: "For any questions about our use of cookies, contact us at:",
        },
      },
    },
  },
};
